import { Button, makeStyles, mergeClasses, MessageBar, MessageBarActions, MessageBarBody, shorthands, tokens } from '@fluentui/react-components'
import * as React from 'react'
import { MutableRefObject, PropsWithChildren, ReactElement, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useBoolean, useConst, useMount } from '@fluentui/react-hooks'
import ReactDropzone, { DropzoneOptions, DropzoneRef, FileRejection } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { getId } from '@fluentui/react'
import { isOfficeInitialized } from '../../helpers/outlookHelper'
import OfficeEventHandlerContext, { OfficeDragAndDropEventArgs } from '../../context/OfficeEventHandlerContext'
import { convertDroppedItemToFile } from '../../helpers/documentHelper'

export const useStyles = makeStyles({
    backdrop: {
        zIndex: 999,
        padding: tokens.spacingVerticalM,
        paddingLeft: 0,
    },
    backdropVisible: {
        background: tokens.colorPaletteBlueBackground2,
        ...shorthands.border('2px', 'dashed', tokens.colorNeutralForeground3),
    },
})

export type DropzoneProps = DropzoneOptions & {
    tipKey?: string
    dropZoneClassname?: string
    dropzoneRef?: MutableRefObject<DropzoneRef>
    onDropAccepted: (files: File[]) => void
    onDropRejected?: (files: FileRejection[]) => void
}

export default function Dropzone(props: PropsWithChildren<DropzoneProps>): ReactElement {
    const officeEventHandlers = useContext(OfficeEventHandlerContext)
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const dropzoneRef = useRef<DropzoneRef>()
    const id = useConst(() => getId('dropzone'))
    const eventHandlerId = useConst(() => getId('dropzoneEventHandler'))

    const [backdrop, { setTrue: openBackdrop, setFalse: closeBackdrop }] = useBoolean(false)
    const [errors, setErrors] = useState<FileRejection[]>()

    const onDropAccepted = useCallback(
        (files: File[]): void => {
            closeBackdrop()
            if (!props.disabled) {
                props.onDropAccepted(files)
            }
        },
        [props.disabled, props.onDropAccepted],
    )

    const onDropRejected = useCallback(
        (files: FileRejection[]): void => {
            closeBackdrop()
            if (!props.disabled) {
                props.onDropRejected?.(files)
                if (!props.onDropRejected) {
                    setErrors(files)
                }
            }
        },
        [props.disabled, props.onDropRejected],
    )

    useEffect(() => {
        if (isOfficeInitialized() && Office.context.platform === Office.PlatformType.OfficeOnline) {
            console.debug('registering drag and drop event handler')
            officeEventHandlers.addHandler({
                id: eventHandlerId,
                eventType: Office.EventType.DragAndDropEvent,
                handle: (event: OfficeDragAndDropEventArgs) => {
                    if (event.dragAndDropEventData.type === 'dragover') {
                        openBackdrop()
                    }

                    // Get the file name and the contents of the items dropped into the task pane.
                    if (event.dragAndDropEventData.type === 'drop') {
                        Promise.all(event.dragAndDropEventData.dataTransfer.files?.map(file => convertDroppedItemToFile(file))).then(files =>
                            onDropAccepted(files),
                        )
                    }
                },
                complete: result => {
                    if (result.status === Office.AsyncResultStatus.Failed) {
                        console.error('Failed to add event handler:', result.error.message)
                        onDropRejected([{ file: undefined, errors: [{ message: result.error.message, code: `${result.error.code}` }] }])
                        return
                    }
                },
            })

            return () => {
                console.debug('unregistering drag and drop event handler')
                officeEventHandlers.removeHandler(eventHandlerId)
            }
        }

        //return noop handler
        return () => {}
    }, [props.disabled])

    useMount(() => {
        window.addEventListener('dragenter', openBackdrop)
        window.addEventListener('dragleave', (event: any) => {
            // console.log(`target ${event.target.id}`)
            let outside: boolean = true
            const dropzone = document.getElementById(id)
            let element = event.target
            while (element && outside) {
                if (element === dropzone) {
                    outside = false
                    continue
                }
                element = element.parentElement
            }
            if (outside) {
                closeBackdrop()
            }
        })
    })

    return (
        <ReactDropzone
            ref={props.dropzoneRef ?? dropzoneRef}
            {...props}
            onDropAccepted={(files: File[]) => {
                onDropAccepted(files)
            }}
            onDropRejected={(files: FileRejection[]) => {
                onDropRejected(files)
            }}
        >
            {({ getRootProps, getInputProps }) => (
                <div
                    {...getRootProps()}
                    id={`${id}`}
                    className={mergeClasses(props.dropZoneClassname, styles.backdrop, !props.disabled && backdrop && styles.backdropVisible)}
                    onClick={closeBackdrop}
                    onContextMenu={closeBackdrop}
                >
                    <input {...getInputProps()} />
                    {props.children}
                    {!props.disabled && (
                        <>
                            {errors && (
                                <>
                                    {errors.map(error => (
                                        <MessageBar key={error.file.path} intent="error" layout={'multiline'}>
                                            <MessageBarBody>
                                                <>{error.errors.map(error => error.message)}</>
                                            </MessageBarBody>
                                        </MessageBar>
                                    ))}
                                </>
                            )}
                            {props.tipKey && (
                                <MessageBar intent="info" layout={'multiline'}>
                                    <MessageBarBody>{translate(props.tipKey)}</MessageBarBody>
                                    <MessageBarActions>
                                        <Button onClick={() => dropzoneRef.current?.open()}>{translate('action.upload')}</Button>
                                    </MessageBarActions>
                                </MessageBar>
                            )}
                        </>
                    )}
                </div>
            )}
        </ReactDropzone>
    )
}
